import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RoleBadge } from "@/components/members/RoleBadge";
import { MemberRole, getRoleDescription } from "@/lib/permissions";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Mail, User, Building2 } from "lucide-react";

interface InvitationData {
  id: string;
  una_id: string;
  una_member_id: string;
  email: string;
  member_role: MemberRole;
  status: string;
  expires_at: string;
  una_name: string;
  member_name: string;
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      validateInvitation(token);
    } else {
      setError("Invalid invitation link");
      setLoading(false);
    }
  }, [searchParams]);

  const validateInvitation = async (token: string) => {
    setLoading(true);
    try {
      console.log("Validating invitation with backend function...");
      
      const { data, error } = await supabase.functions.invoke('validate-invitation', {
        body: { token }
      });

      if (error || !data) {
        console.error("Validation error:", error);
        setError(data?.error || error?.message || "Invalid or expired invitation");
        setLoading(false);
        return;
      }

      console.log("Invitation validated successfully:", data);
      setInvitation(data);
    } catch (error: any) {
      console.error("Error validating invitation:", error);
      setError("Failed to validate invitation");
    }
    setLoading(false);
  };

  const handleAcceptInvitation = async () => {
    if (!invitation) return;

    // Validation
    if (password.length < 8) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Password must be at least 8 characters long",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Passwords do not match",
      });
      return;
    }

    if (!termsAccepted) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please accept the terms and conditions",
      });
      return;
    }

    setSubmitting(true);

    try {
      console.log("Checking for existing auth account...");
      
      // First check if user is already logged in
      const { data: { session } } = await supabase.auth.getSession();
      let userId: string | null = null;

      if (session?.user?.email === invitation.email) {
        // User is already logged in with the correct email
        console.log("User already logged in with matching email");
        userId = session.user.id;
      } else {
        // Try to create a new account or log in
        console.log("Attempting to sign in with existing credentials...");
        
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: invitation.email,
          password: password,
        });

        if (signInError) {
          // If sign in fails, try to create new account
          if (signInError.message.includes("Invalid login credentials")) {
            console.log("No existing account found, creating new one...");
            
            const { data: authData, error: authError } = await supabase.auth.signUp({
              email: invitation.email,
              password: password,
              options: {
                emailRedirectTo: `${window.location.origin}/member-dashboard`,
              },
            });

            if (authError) {
              throw authError;
            }

            if (!authData.user) {
              throw new Error("Failed to create account");
            }

            userId = authData.user.id;
          } else {
            throw signInError;
          }
        } else {
          // Successfully signed in
          console.log("Successfully signed in with existing account");
          userId = signInData.user.id;
        }
      }

      if (!userId) {
        throw new Error("Failed to authenticate user");
      }

      console.log("Accepting invitation via backend...");

      // Call backend function to handle acceptance
      const { data: acceptData, error: acceptError } = await supabase.functions.invoke(
        'accept-member-invitation',
        {
          body: {
            token: searchParams.get("token"),
            user_id: userId,
            user_email: invitation.email,
          },
        }
      );

      if (acceptError || !acceptData?.success) {
        console.error("Acceptance error:", acceptError);
        throw new Error(acceptData?.error || acceptError?.message || "Failed to accept invitation");
      }

      console.log("Invitation accepted successfully");

      toast({
        title: "Success!",
        description: "Your account has been linked. Redirecting to dashboard...",
      });

      // Redirect to member dashboard
      setTimeout(() => {
        navigate("/member-dashboard");
      }, 2000);
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to accept invitation",
      });
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-center">Invitation Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error || "Invalid invitation"}</AlertDescription>
            </Alert>
            <Button className="w-full mt-4" onClick={() => navigate("/login")}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-3xl">You're Invited!</CardTitle>
          <CardDescription>
            Create your account to join {invitation.una_name}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="space-y-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Organization</p>
                <p className="font-semibold">{invitation.una_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Your Name</p>
                <p className="font-semibold">{invitation.member_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-semibold">{invitation.email}</p>
              </div>
            </div>
          </div>

          {/* Role Information */}
          <div className="space-y-2">
            <Label>Your Assigned Role</Label>
            <div className="flex items-center gap-2">
              <RoleBadge role={invitation.member_role} size="large" />
            </div>
            <p className="text-sm text-muted-foreground">
              {getRoleDescription(invitation.member_role)}
            </p>
          </div>

          {/* Password Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
            </div>
          </div>

          {/* Terms Acceptance */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
            />
            <label
              htmlFor="terms"
              className="text-sm text-muted-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I accept the terms and conditions and agree to join this UNA as a member
            </label>
          </div>

          {/* Submit Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleAcceptInvitation}
            disabled={submitting}
          >
            {submitting ? (
              "Creating Account..."
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Accept Invitation & Create Account
              </>
            )}
          </Button>

          {/* Expiry Notice */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This invitation expires on{" "}
              {new Date(invitation.expires_at).toLocaleDateString()}. Please accept it
              soon to gain access.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
